import { useEffect, useState } from 'react';
import styles from './TestsPage.module.css';
import API from '../../../Utils/api';
import { useUser } from '../../../Context/useUser';
import { useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import Table from '../../../Components/Table/Table';
import Utils from '../../../Utils';
import { calculateAerobicTrainingZones, calculateAnaerobicTrainingZones } from './zones';
import { toast } from 'react-toastify';

const AEROBIC_ZONES = [100, 200, 300, 400];

const ANAEROBIC_ZONES = [25, 50];

export default function TrainingZonesPage () {
    const { user } = useUser();

    const { athleteId } = useParams();

    const visitedUser = useVisitingUser(athleteId);

    const currentUser = athleteId ? visitedUser : user;

    const [tests, setTests] = useState([]);

    const [selectedTest, setSelectedTest] = useState();

    useEffect(() => {
        if (tests.length > 0) {
            setSelectedTest(tests[0]);
        }
    }, [tests]);

    const handleTestSelection = event => {
        const selectedTestId = event.target.value;
        const test = tests.find(test => test._id === selectedTestId);
        setSelectedTest(test);
    };

    const [selectedAerobicZone, setSelectedAerobicZone] = useState(AEROBIC_ZONES[0]);

    const handleAerobicZoneChange = event => {
        const selectedZone = parseInt(event.target.value, 10);
        setSelectedAerobicZone(selectedZone);
    };

    const handleAnaerobicZoneChange = event => {
        const selectedZone = parseInt(event.target.value, 10);
        setSelectedAnaerobicZone(selectedZone);
    };

    const [selectedAnaerobicZone, setSelectedAnaerobicZone] = useState(ANAEROBIC_ZONES[0]);

    const aerobicColumns = [
        {
            id: 'title',
            Header: '',
            accessor: 'title',
            Cell: ({ row }) => <div className={styles.zoneTitle}>{row.original.title}</div>
        },
        {
            id: 'subTitle',
            Header: '',
            accessor: 'subTitle',
            Cell: ({ row }) => <div>{row.original.subTitle}</div>
        },
        {
            id: 'paceGroup',
            Header: () => <div style={{ textAlign: 'center' }}>Pace</div>,
            columns: [
                {
                    id: 'timeFrom',
                    Header: () => <div style={{ textAlign: 'center' }}>Time From</div>,
                    accessor: 'timeFrom',
                    Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.timeFrom}</div>
                },
                {
                    id: 'timeTo',
                    Header: () => <div style={{ textAlign: 'center' }}>Time To</div>,
                    accessor: 'timeTo',
                    Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.timeTo}</div>
                },
                {
                    id: 'averagePace',
                    Header: () => <div style={{ textAlign: 'center' }}>Average</div>,
                    accessor: 'averagePace',
                    Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.averagePace}</div>
                }
            ]
        },
        {
            id: 'totalDuration',
            Header: () => <div style={{ textAlign: 'center' }}>Total Duration</div>,
            accessor: 'totalDuration',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.totalDuration ?? 'N/A for 100m'}</div>
        }
    ];

    const anaerobicColumns = [
        {
            Header: '',
            accessor: 'title',
            Cell: ({ row }) => <div className={styles.zoneTitle}>{row.original.title}</div>
        },
        {
            Header: '',
            accessor: 'subTitle',
            Cell: ({ row }) => <div>{row.original.subTitle}</div>
        },
        {
            id: 'timeFrom',
            Header: () => <div style={{ textAlign: 'center' }}>Time From</div>,
            accessor: 'timeFrom',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.timeFrom}</div>
        },
        {
            id: 'timeTo',
            Header: () => <div style={{ textAlign: 'center' }}>Time To</div>,
            accessor: 'timeTo',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.timeTo}</div>
        },
        {
            id: 'averagePace',
            Header: () => <div style={{ textAlign: 'center' }}>Average</div>,
            accessor: 'averagePace',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.averagePace}</div>
        }
    ];

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    const fetchAndSetData = async currentUser => {
        if (!currentUser) return;

        try {
            const { data } = await API.getAllTestsForUser(currentUser._id);
            if (data) {
                setTests(data);
            }
        } catch (error) {
            toast.error(`Failed to fetch tests - please contact support`, {
                position: 'bottom-center'
            });
        }
    };

    return (
        <div className={styles.trainingZonesPage}>
            <div className={styles.topBar + ' island'}>
                <div className={styles.topBarSelection}>
                    <span className={styles.testDatesTitle}>Selected Test:</span>
                    <select
                        className={styles.selectTestDropdown + ' form-control'}
                        onChange={handleTestSelection}
                    >
                        {tests.map(test => (
                            <option key={test._id} value={test._id}>
                                {test.type} - {new Date(test.testDate).toLocaleDateString()} -{' '}
                                {'CSS: ' + test.css?.toFixed(2)} -{' '}
                                {'MSS: ' + test.mss?.toFixed(2)}
                            </option>
                        ))}
                    </select>
                </div>
                <span className={styles.selectTestInfo}>
                    Please select a test from the dropdown
                </span>
            </div>
            <div className={styles.zonesBlock + ' island'}>
                <span className={styles.zoneTitle}>Aerobic Zones</span>
                <div className={styles.zonesDistanceSelect}>
                    <span className={styles.zoneDistance}>Distance:</span>
                    <select onChange={handleAerobicZoneChange} className='form-control'>
                        {AEROBIC_ZONES.map(zone => (
                            <option key={zone} value={zone}>
                                {zone}m
                            </option>
                        ))}
                    </select>
                </div>
                <Table
                    className={styles.zonesTable}
                    columns={aerobicColumns}
                    data={Utils.arrayfyObject(
                        calculateAerobicTrainingZones(selectedTest?.css, selectedAerobicZone),
                        {
                            keyToName: 'name'
                        }
                    )}
                    updatePageSize={false}
                    pageSkipping={false}
                    showSelectionIndicator={false}
                    tableLayout='fixed'
                />
            </div>
            <div className={styles.zonesBlock + ' island'}>
                <span className={styles.zoneTitle}>Anaerobic Zones</span>
                <div className={styles.zonesDistanceSelect}>
                    <span className={styles.zoneDistance}>Distance:</span>
                    <select onChange={handleAnaerobicZoneChange} className='form-control'>
                        {ANAEROBIC_ZONES.map(zone => (
                            <option key={zone} value={zone}>
                                {zone}m
                            </option>
                        ))}
                    </select>
                </div>
                <Table
                    className={styles.zonesTable}
                    columns={anaerobicColumns}
                    data={Utils.arrayfyObject(
                        calculateAnaerobicTrainingZones(selectedTest?.mss, selectedAnaerobicZone),
                        {
                            keyToName: 'name'
                        },
                    )}
                    updatePageSize={false}
                    pageSkipping={false}
                    showSelectionIndicator={false}
                    tableLayout='fixed'
                />
            </div>
        </div>
    );
}
